import { useEffect, useRef, useState } from "react"
import * as echarts from "echarts/core"
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
} from "echarts/components"
import { BarChart, LineChart } from "echarts/charts"
import { UniversalTransition } from "echarts/features"
import { CanvasRenderer } from "echarts/renderers"
import { isMobile } from "react-device-detect"
import NoDataFound from '../../NoDataFound'

echarts.use([
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
  UniversalTransition,
])

const InstituteCreditLineChart = ({ data }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  // Check if data is null, undefined, or empty array
  const isDataEmpty = !data || data.length === 0;

  useEffect(() => {
    if (chartRef.current && !isDataEmpty) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      const yearlyData = data.reduce((acc, item) => {
        const year = new Date(item.date).getFullYear();
        const amount = parseFloat(item.amount);

        if (!acc[year]) {
          acc[year] = amount;
        } else {
          acc[year] += amount;
        }

        return acc;
      }, {});

      const chartYears = Object.keys(yearlyData);
      const chartAmounts = Object.values(yearlyData);

      const option = {
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const year = params[0].axisValue;
            const value = params[0].value.toLocaleString();
            let tooltipContent = `<div style="padding: 10px;">`
            tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; color: #495057;">📅 ${year}</div>`

            params.forEach((param) => {
              const dataIndex = param.dataIndex
              const transaction = data[dataIndex]

              if (param.seriesName === "Amount") {
                tooltipContent += `<div style="margin-bottom: 4px;">`
                tooltipContent += `<span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>`
                tooltipContent += `<strong>Amount Spent:</strong> ₹${value}`
                tooltipContent += `</div>`
                tooltipContent += `<div style="margin-bottom: 4px; color: #6c757d; font-size: 12px;">`
                // tooltipContent += `Transaction Type: ${transaction.type}`
                tooltipContent += `</div>`
                tooltipContent += `<div style="margin-bottom: 4px; color: #6c757d; font-size: 12px;">`
                tooltipContent += `Status: <span style="color: ${transaction.status === "completed" ? "#28a745" : "#ffc107"};">${transaction.status.toUpperCase()}</span>`
                tooltipContent += `</div>`
              }
            })

            tooltipContent += `</div>`
            return tooltipContent
          },
          confine: true
        },
        legend: { data: ["Amount"], top: isMobile ? 5 : 30 },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: {
          type: "category",
          data: chartYears,
          axisLabel: { fontSize: 12 },
        },
        yAxis: {
          type: "value",
          name: "Amount (₹)",
          axisLabel: {
            formatter: (value) => "₹" + value / 1000 + "K",
          },
        },
        series: [
          {
            name: "Amount",
            type: "bar",
            data: chartAmounts,
            itemStyle: {
              color: "#dc3545",
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: "#c82333",
              },
            },
            barWidth: data.length === 1 ? '10%' : "15%",
          },
        ],
      };

      chartInstance.current.setOption(option);
      const handleResize = () => chartInstance.current && chartInstance.current.resize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, isDataEmpty]);

  // Show "Data not available" message when data is empty
  if (isDataEmpty) {
    return (
      <NoDataFound message='Data not available' height='100%' />
    );
  }

  return (
    <div ref={chartRef} style={{ width: '100%', height: '320px' }} />
  )
}

export default InstituteCreditLineChart;